import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

// The routes keep /apps/casework/* from the app's working name.
export const APP_NAME = 'Satchel';
const SUPPORT_EMAIL = 'support@socialworktestprepacademy.com';
const EFFECTIVE_DATE = 'October 13, 2026';
const ORIGIN = 'https://www.socialworktestprepacademy.com';
const PRIVACY_PATH = '/apps/casework/privacy';
const SUPPORT_PATH = '/apps/casework/support';

const p = 'mb-4 text-neutral-700';
const h2 = 'text-xl font-semibold mb-3 text-neutral-900';

const SupportLink: React.FC = () => (
  <a className="text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
);

const Page: React.FC<{ path: string; title: string; description: string; children: React.ReactNode }> = ({
  path,
  title,
  description,
  children,
}) => (
  <>
    <Helmet>
      <title>{`${title} | Social Work Test Prep Academy`}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${ORIGIN}${path}`} />
      <meta property="og:site_name" content="Social Work Test Prep Academy" />
      <link rel="canonical" href={`${ORIGIN}${path}`} />
    </Helmet>
    <div className="new-home-container">
      <div className="new-home-content">
        <div className="new-home-inner">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center text-neutral-900">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  </>
);

export const CaseworkPrivacy: React.FC = () => (
  <Page
    path={PRIVACY_PATH}
    title={`${APP_NAME} Privacy Policy`}
    description={`${APP_NAME} runs entirely on your iPhone. No account, no server, and the app sends us nothing. Here is what leaves the phone, and when.`}
  >
    <p className="text-base sm:text-lg mb-6 text-center text-neutral-600">Effective {EFFECTIVE_DATE}</p>

    <h2 className={h2}>The short version</h2>
    <p className={p}>
      {APP_NAME} is an iPhone app for social workers, made by Social Work Test Prep Academy. It runs entirely on
      your phone. There is no account, we run no server for it, and the app sends us nothing. We do not collect
      any data from it. Its App Store privacy label is &quot;Data Not Collected.&quot;
    </p>
    <p className={p}>
      One exception while the app is in testing: if you test it through TestFlight, Apple may send us crash
      reports and any feedback you choose to submit through TestFlight. Those come from Apple, under Apple&apos;s
      policies, and never include your record.
    </p>

    <h2 className={h2}>What stays on your phone</h2>
    <p className={p}>
      Everything you enter, including your case notes and client details, is stored encrypted on the device and
      excluded from iCloud backup. You can lock the app with Face ID or your passcode, and you can erase
      everything in the app&apos;s Settings. We cannot see, recover or restore it.
    </p>

    <h2 className={h2}>What can leave your phone</h2>
    <ul className="list-disc pl-5 mb-4 text-neutral-700">
      <li className="mb-2">
        <strong>Documents you choose to share.</strong> When you export, print or share a document, it goes
        wherever you send it. Nothing is sent unless you do this.
      </li>
      <li className="mb-2">
        <strong>Resource searches.</strong> To find nearby services, the app sends Apple Maps the area you
        enter for the search (a city, neighborhood or ZIP) or your current location, plus fixed category words
        such as &quot;food pantry&quot;. For some needs it also asks US federal directories: SAMHSA
        FindTreatment, HRSA health centers, HUD housing counselors and housing authorities, and Legal Services
        Corporation offices. Those directories get only that location rounded to about a kilometer. A search
        never includes a client&apos;s name, notes or anything else from a case.
      </li>
      <li className="mb-2">
        <strong>Dictation.</strong> Speech is turned into text on the phone by Apple&apos;s on-device speech
        recognition. Apple may download its speech model to your phone to do this.
      </li>
      <li className="mb-2">
        <strong>Government forms.</strong> These are the agencies&apos; own PDFs, bundled in the app and filled
        in on the phone. The app does not submit them anywhere.
      </li>
    </ul>
    <p className={p}>
      Apple and those agencies handle what they receive under their own privacy policies.
    </p>

    <h2 className={h2}>AI drafts and decisions</h2>
    <p className={p}>
      Drafts the app writes for you are not added to the record until you review and approve them. The app never
      sets a risk level or decides anything about a client. Those calls stay with you.
    </p>

    <h2 className={h2}>Your agency&apos;s rules come first</h2>
    <p className={p}>
      Whether you may keep real client details in an app on a personal device is up to your agency&apos;s
      policy, not us. The app says so the first time you open it. Check with your agency before you enter real
      client information.
    </p>

    <h2 className={h2}>Children</h2>
    <p className={p}>{APP_NAME} is a tool for working professionals and is not meant for children.</p>

    <h2 className={h2}>Changes</h2>
    <p className={p}>
      If this policy changes, we will update this page and its effective date.
    </p>

    <h2 className={h2}>Contact</h2>
    <p className={p}>
      Questions about this policy: <SupportLink />. Help with the app is on the{' '}
      <Link className="text-primary-600 hover:underline" to={SUPPORT_PATH}>{APP_NAME} support page</Link>.
    </p>
  </Page>
);

const FAQ: { q: string; a: React.ReactNode }[] = [
  {
    q: `What is ${APP_NAME}?`,
    a: `An iPhone app that helps social workers keep case notes, draft documents, fill in government forms and find local resources. It runs entirely on your phone, with no account.`,
  },
  {
    q: 'What is it not?',
    a: `It is not your agency's system of record, so keep your official records where your agency requires. It does not submit government forms; you fill them in on the phone and submit them yourself. It does not give clinical advice, set risk levels or make decisions about a client.`,
  },
  {
    q: 'Can I use real client information?',
    a: `That is your agency's decision, not ours. Check its policy on personal devices before you enter real client details.`,
  },
  {
    q: 'How do I erase my data?',
    a: `Open the app's Settings and erase all data. Because nothing is stored with us or in iCloud backup, it cannot be recovered afterward.`,
  },
  {
    q: 'Why does it have names and cases I did not enter?',
    a: `The current test build comes with invented demo data so you can try it out. None of it is about real people.`,
  },
  {
    q: 'How do I report a problem?',
    a: (
      <>
        Email <SupportLink /> with what happened, your iPhone model and iOS version. Please do not include client
        names or any case details. If you test through TestFlight, Apple may also send us crash reports and any
        feedback you submit there; those come from Apple and never include your record.
      </>
    ),
  },
];

export const CaseworkSupport: React.FC = () => (
  <Page
    path={SUPPORT_PATH}
    title={`${APP_NAME} Support`}
    description={`Get help with ${APP_NAME}, the on-device iPhone app for social workers: what it does, what it does not, how to erase data and how to reach us.`}
  >
    <p className={`${p} text-center`}>
      Questions or problems: email <SupportLink />.
    </p>

    {FAQ.map(({ q, a }) => (
      <section key={q} className="mb-6">
        <h2 className={h2}>{q}</h2>
        <p className={p}>{a}</p>
      </section>
    ))}

    <p className={p}>
      How the app handles information is in the{' '}
      <Link className="text-primary-600 hover:underline" to={PRIVACY_PATH}>{APP_NAME} privacy policy</Link>.
    </p>
  </Page>
);
